/**
 * 簡訊邀請「中繼頁」(/sms-invite/open) 的純函式。
 *
 * 背景：NCC 2024-11 起商業簡訊內的網址必須是自有網域，`liff.line.me` 會被電信擋下
 * （三竹 portal 顯示「資料有錯誤」）。所以簡訊只能放自有網域，再由本頁把客戶帶進
 * `https://liff.line.me/{瓦斯行 liff_main_id}/sms-invite?…`，讓註冊在 LINE app 內、
 * 用瓦斯行自己的 LIFF/channel 完成。
 *
 * 流程：
 *   簡訊 → /sms-invite/open?liffId=X&phone=…&invite=…[&k=…][&order_id][&src]（本頁，不初始化 LIFF）
 *        → 自動嘗試 line://app/X?liff.state=/sms-invite?…（自訂 scheme，程式導向也能喚起 LINE）
 *          ＋按鈕 liff.line.me/X/sms-invite?…（親手點才會觸發 Universal Link）
 *        → LINE 開 endpoint（/?liffId=X&liff.state=/sms-invite?…）→ /sms-invite 落地頁
 *   落地頁路徑是 /sms-invite，不是 /sms-invite/open，不會迴圈。
 *
 * 迴圈保險（shouldAutoOpenLiff）：網址帶 liff.state、或從 liff.line.me 轉來、或本 session
 * 已對同一邀請碼自動跳過一次 → 不再自動跳，只顯示按鈕。
 */
const LIFF_URL_PREFIX = "https://liff.line.me";
/** LINE app 自訂 scheme（LIFF v1 格式，官方標示 deprecated 但 LINE app 仍支援）；見 buildSmsInviteLineSchemeUrl。 */
const LINE_SCHEME_PREFIX = "line://app";
const SMS_INVITE_PATH = "/sms-invite";
const AUTO_OPENED_KEY_PREFIX = "sms_invite_auto_opened:";

export interface SmsInviteOpenParams {
  /** 瓦斯行的 LIFF ID（supplier_line_settings.liff_main_id）；空字串＝簡訊沒帶，退回預設 LIFF */
  liffId: string;
  phone?: string;
  invite: string;
  /** LINE 推送路徑的 link token */
  k?: string;
  order_id?: string;
  src?: string;
}

const clean = (raw: string | null | undefined): string => (raw ?? "").trim();

export const isSmsInviteOpenMode = (
  pathname: string = typeof window !== "undefined" ? window.location.pathname : "",
): boolean => /^\/sms-invite\/open\/?$/.test(pathname);

/** 讀網址參數；invite 必填，phone（簡訊）或 k（LINE 推送）其一必填，否則 null。 */
export const parseSmsInviteOpenParams = (
  search: string = typeof window !== "undefined" ? window.location.search : "",
): SmsInviteOpenParams | null => {
  const params = new URLSearchParams(search);
  const phone = clean(params.get("phone"));
  const invite = clean(params.get("invite"));
  const k = clean(params.get("k"));
  // invite 必填；phone（簡訊）或 k（LINE 推送）其一
  if (!invite || (!phone && !k)) return null;
  const result: SmsInviteOpenParams = { liffId: clean(params.get("liffId")), invite };
  if (phone) result.phone = phone;
  if (k) result.k = k;
  const orderId = clean(params.get("order_id"));
  if (orderId) result.order_id = orderId;
  const src = clean(params.get("src"));
  if (src) result.src = src;
  return result;
};

/** 落地頁 query（順序固定：liffId → phone → invite → k → order_id → src），與 consumer / aw-api 的 buildSmsInviteUrl 一致。 */
const buildLandingQuery = (p: SmsInviteOpenParams, withLiffId: boolean): string => {
  const q = new URLSearchParams();
  if (withLiffId && p.liffId) q.set("liffId", p.liffId);
  if (p.phone) q.set("phone", p.phone);
  q.set("invite", p.invite);
  if (p.k) q.set("k", p.k);
  if (p.order_id) q.set("order_id", p.order_id);
  if (p.src) q.set("src", p.src);
  return q.toString();
};

/** `https://liff.line.me/{liffId}/sms-invite?…`；沒 liffId 組不出來 → null。 */
export const buildSmsInviteLiffUrl = (p: SmsInviteOpenParams): string | null => {
  if (!p.liffId) return null;
  return `${LIFF_URL_PREFIX}/${p.liffId}${SMS_INVITE_PATH}?${buildLandingQuery(p, false)}`;
};

/**
 * `line://app/{liffId}?liff.state=%2Fsms-invite%3F…`：LINE app 自訂 scheme 版的邀請連結。
 *
 * 為什麼要多這一條：中繼頁的「自動跳轉」是程式改網址（location.href），iOS 對
 * liff.line.me 這種 Universal Link 只在使用者親手點連結時才會喚起 App，程式導向會被當成
 * 一般網頁在 Safari 載入，接著 LIFF 在外部瀏覽器走 LINE Login，客戶就卡在 access.line.me
 * 的登入頁。自訂 scheme 不受這條限制：有裝 LINE 時系統會問「要用 LINE 開啟嗎」；沒裝
 * 時導向失敗、頁面留在原地，客戶仍看得到按鈕與「在瀏覽器繼續」。
 *
 * 為什麼路徑塞在 liff.state 而不是放 URL path：`line://app/{liffId}` 是 LIFF v1 格式
 * （官方標示 deprecated 但 LINE app 仍支援），v1 只保證 query 會帶進 LIFF，路徑段
 * （/sms-invite）會不會保留沒有文件。把「/sms-invite?phone=…」整包放進 liff.state，
 * 不論 LINE 有沒有保留路徑，只要 query 有帶到，LIFF SDK 與 LineLoginProvider
 * （resolveEffectiveLocation）都會拿 liff.state 當唯一真相導去落地頁——這正是
 * liff.line.me 本來就在做的事。
 *
 * 按鈕本身仍用 https 版（buildSmsInviteLiffUrl）：親手點 Universal Link 是最穩的路，
 * 而且沒裝 LINE 時 https 版至少還打得開網頁。
 */
export const buildSmsInviteLineSchemeUrl = (p: SmsInviteOpenParams): string | null => {
  if (!p.liffId) return null;
  const q = new URLSearchParams();
  q.set("liff.state", `${SMS_INVITE_PATH}?${buildLandingQuery(p, false)}`);
  return `${LINE_SCHEME_PREFIX}/${p.liffId}?${q.toString()}`;
};

/** 本站落地路徑 `/sms-invite?liffId=…&phone=…`；帶 liffId 讓 getRuntimeLiffId 用瓦斯行 LIFF 初始化。 */
export const buildSmsInviteBrowserPath = (p: SmsInviteOpenParams): string =>
  `${SMS_INVITE_PATH}?${buildLandingQuery(p, true)}`;

const autoOpenedKey = (invite: string): string => `${AUTO_OPENED_KEY_PREFIX}${invite}`;

export const markSmsInviteAutoOpened = (invite: string): void => {
  try {
    window.sessionStorage.setItem(autoOpenedKey(invite), "1");
  } catch {
    // 忽略：sessionStorage 不可用時只是少一道保險
  }
};

const wasAutoOpened = (invite: string): boolean => {
  try {
    return window.sessionStorage.getItem(autoOpenedKey(invite)) === "1";
  } catch {
    return false;
  }
};

/**
 * 是否要自動導向 liff.line.me。三種情況一律不自動跳、只留按鈕：
 *  1. 網址帶 `liff.state`（這是 LIFF 回跳，不該再出去）
 *  2. referrer 來自 liff.line.me（剛從 LIFF 轉回來）
 *  3. 本 session 已對同一邀請碼自動跳過一次
 */
export const shouldAutoOpenLiff = (
  search: string = typeof window !== "undefined" ? window.location.search : "",
  referrer: string = typeof document !== "undefined" ? document.referrer : "",
): boolean => {
  const params = new URLSearchParams(search);
  if (params.has("liff.state")) return false;
  if (/^https?:\/\/liff\.line\.me(\/|$)/i.test(referrer)) return false;
  const invite = clean(params.get("invite"));
  if (invite && wasAutoOpened(invite)) return false;
  return true;
};
